// src/auth/useSubscription.js
// Reads the signed-in user's `profiles` row (subscription state) and exposes
// Stripe checkout / billing-portal launchers.
import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../utils/supabaseClient";
import { useAuth } from "./AuthContext";

const SUBSCRIBER_STATUSES = ["trialing", "active", "past_due"];

export function useSubscription() {
  const { user, session, loading: authLoading } = useAuth();
  const userId = user ? user.id : null;

  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // Guards against out-of-order responses when the user changes quickly.
  const requestIdRef = useRef(0);

  const refresh = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    if (!userId) {
      setProfile(null);
      setProfileLoading(false);
      return;
    }
    setProfileLoading(true);
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();
    if (requestId !== requestIdRef.current) return; // stale response
    if (error) {
      console.error("useSubscription: failed to load profile:", error.message);
      setProfile(null);
    } else {
      setProfile(data);
    }
    setProfileLoading(false);
  }, [userId]);

  // Re-fetch whenever the auth user changes (null user -> profile null).
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Returning from Stripe checkout (?billing=success) — the webhook that
  // updates the profile can lag a moment, so re-fetch again shortly after.
  useEffect(() => {
    if (!userId) return undefined;
    if (!window.location.search.includes("billing=success")) return undefined;
    const timer = setTimeout(() => {
      refresh();
    }, 2500);
    return () => clearTimeout(timer);
  }, [userId, refresh]);

  const callBillingFunction = useCallback(
    async (name) => {
      const accessToken = session ? session.access_token : null;
      if (!accessToken) {
        return { error: "You need to sign in first." };
      }
      try {
        const res = await fetch(`/.netlify/functions/${name}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ returnUrl: window.location.href }),
        });
        let data = {};
        try {
          data = await res.json();
        } catch (e) {
          data = {};
        }
        if (!res.ok || !data.url) {
          const message =
            data.error || `Billing request failed (${res.status})`;
          console.error(`${name} error:`, message);
          return { error: message };
        }
        window.location.assign(data.url);
        return { error: null };
      } catch (err) {
        console.error(`${name} error:`, err);
        return { error: err.message || "Billing request failed." };
      }
    },
    [session]
  );

  const startCheckout = useCallback(
    () => callBillingFunction("billing-checkout"),
    [callBillingFunction]
  );

  const openPortal = useCallback(
    () => callBillingFunction("billing-portal"),
    [callBillingFunction]
  );

  const status = (profile && profile.subscription_status) || "none";
  // Admins (profiles.is_admin) get full premium access without a subscription;
  // the RLS helper user_has_active_subscription() applies the same rule.
  const isAdmin = !!(profile && profile.is_admin);
  const isSubscriber = isAdmin || SUBSCRIBER_STATUSES.includes(status);

  return {
    loading: authLoading || profileLoading,
    profile,
    status,
    isAdmin,
    isSubscriber,
    refresh,
    startCheckout,
    openPortal,
  };
}

export default useSubscription;
